import { CONFERENCE_FAILED, CONFERENCE_JOINED, CONFERENCE_LEFT, CONFERENCE_WILL_LEAVE, KICKED_OUT } from '../base/conference/actionTypes';
import { PARTICIPANT_JOINED, PARTICIPANT_LEFT } from '../base/participants/actionTypes';
import { getLocalParticipant, getParticipantCount } from '../base/participants/functions';
import MiddlewareRegistry from '../base/redux/MiddlewareRegistry';
import { clearActiveMeetingId, getAccessToken, getActiveMeetingId } from './authStorage';
import { getPortalApiBaseUrl } from './config';
import logger from './logger';
import { isHostEndedForAll, resetHostEndedForAll } from './meetingLifecycle';
// 避免 CONFERENCE_WILL_LEAVE / LEFT / pagehide 重复上报。
let leaveReported = false;
let pageHideBound = false;
// 占用上报防抖与心跳。
let occupancyTimer;
let occupancyHeartbeat;
let lastReportedCount;
const OCCUPANCY_DEBOUNCE_MS = 400;
const OCCUPANCY_HEARTBEAT_MS = 60000;
/**
 * 使用 keepalive 上报离会，避免移动端挂断/跳转时请求被浏览器取消。.
 *
 * @param {string} reason - 触发原因.
 * @param {Object} [options] - 可选参数.
 * @param {boolean} [options.skipIfReplaced] - 设备替换时跳过 leave.
 * @returns {void}
 */
function reportPortalLeave(reason, options = {}) {
    if (leaveReported) {
        return;
    }
    // 主持人已走 /end：立刻 ENDED，不再按个人 leave / occupancy=0 处理
    if (isHostEndedForAll()) {
        leaveReported = true;
        stopOccupancyHeartbeat();
        clearActiveMeetingId();
        logger.info(`[${new Date().toISOString()}] 主持人已全员结束，跳过门户 leave | reason=${reason}`);
        return;
    }
    // flip 踢旧：旧端被替换时不关门户会话，避免误关新设备 history
    if (options.skipIfReplaced) {
        logger.info(`设备替换踢出，跳过门户离会 | reason=${reason}`);
        clearActiveMeetingId();
        leaveReported = true;
        stopOccupancyHeartbeat();
        return;
    }
    const meetingId = getActiveMeetingId();
    const accessToken = getAccessToken();
    if (!meetingId || !accessToken) {
        logger.warn(`跳过门户离会上报 | reason=${reason} meetingId=${meetingId} hasToken=${Boolean(accessToken)}`);
        clearActiveMeetingId();
        stopOccupancyHeartbeat();
        return;
    }
    leaveReported = true;
    stopOccupancyHeartbeat();
    // 离会前先报占用=0，启动空房计时（与 leave 并行，keepalive）
    reportOccupancyRaw(meetingId, accessToken, 0, `${reason}:occupancy0`, true);
    const url = `${getPortalApiBaseUrl()}/api/meetings/${meetingId}/leave`;
    logger.info(`会中离开，上报门户离会 | reason=${reason} meetingId=${meetingId}`);
    try {
        fetch(url, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
            },
            keepalive: true,
            body: '{}'
        }).then(response => {
            if (response.ok || response.status === 204) {
                logger.info(`门户离会上报成功 | reason=${reason} meetingId=${meetingId}`);
            }
            else {
                logger.warn(`门户离会上报失败 | reason=${reason} meetingId=${meetingId} status=${response.status}`);
            }
        }).catch((error) => {
            logger.warn(`门户离会上报异常 | reason=${reason} meetingId=${meetingId} error=${error?.message || error}`);
        });
    }
    catch (error) {
        logger.warn(`门户离会上报抛出 | reason=${reason} meetingId=${meetingId} error=${error?.message || error}`);
    }
    finally {
        clearActiveMeetingId();
    }
}
/**
 * 向门户 POST occupancy。.
 *
 * @param {number} meetingId - 门户会议 ID。.
 * @param {string} accessToken - 访问令牌。.
 * @param {number} participantCount - 会中人数。.
 * @param {string} reason - 日志原因。.
 * @param {boolean} keepalive - 是否使用 fetch keepalive。.
 * @returns {void}
 */
function reportOccupancyRaw(meetingId, accessToken, participantCount, reason, keepalive = false) {
    const url = `${getPortalApiBaseUrl()}/api/meetings/${meetingId}/occupancy`;
    logger.info(`上报会中占用 | reason=${reason} meetingId=${meetingId} count=${participantCount}`);
    try {
        fetch(url, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
            },
            keepalive,
            body: JSON.stringify({ participantCount })
        }).then(response => {
            if (response.ok || response.status === 204) {
                lastReportedCount = participantCount;
                logger.info(`会中占用上报成功 | reason=${reason} meetingId=${meetingId} count=${participantCount}`);
            }
            else {
                logger.warn(`会中占用上报失败 | reason=${reason} meetingId=${meetingId} `
                    + `count=${participantCount} status=${response.status}`);
            }
        }).catch((error) => {
            logger.warn(`会中占用上报异常 | reason=${reason} meetingId=${meetingId} `
                + `error=${error?.message || error}`);
        });
    }
    catch (error) {
        logger.warn(`会中占用上报抛出 | reason=${reason} meetingId=${meetingId} error=${error?.message || error}`);
    }
}
/**
 * 按当前 Redux 参会者人数上报占用（与侧栏同源 getParticipantCount）。.
 *
 * @param {IStore} store - Redux store。.
 * @param {string} reason - 触发原因。.
 * @param {boolean} immediate - 是否跳过防抖立即发送。.
 * @returns {void}
 */
function scheduleOccupancyReport(store, reason, immediate = false) {
    if (leaveReported || isHostEndedForAll()) {
        return;
    }
    const meetingId = getActiveMeetingId();
    const accessToken = getAccessToken();
    if (!meetingId || !accessToken) {
        return;
    }
    const send = () => {
        if (leaveReported || isHostEndedForAll()) {
            return;
        }
        const count = getParticipantCount(store.getState());
        if (!immediate && lastReportedCount === count) {
            return;
        }
        reportOccupancyRaw(meetingId, accessToken, count, reason, false);
    };
    if (immediate) {
        if (occupancyTimer) {
            clearTimeout(occupancyTimer);
            occupancyTimer = undefined;
        }
        send();
        return;
    }
    if (occupancyTimer) {
        clearTimeout(occupancyTimer);
    }
    occupancyTimer = setTimeout(() => {
        occupancyTimer = undefined;
        send();
    }, OCCUPANCY_DEBOUNCE_MS);
}
/**
 * 启动占用心跳。.
 *
 * @param {IStore} store - Redux store。.
 * @returns {void}
 */
function startOccupancyHeartbeat(store) {
    stopOccupancyHeartbeat();
    occupancyHeartbeat = setInterval(() => {
        scheduleOccupancyReport(store, 'heartbeat', true);
    }, OCCUPANCY_HEARTBEAT_MS);
}
/**
 * 停止占用心跳与防抖定时器。.
 *
 * @returns {void}
 */
function stopOccupancyHeartbeat() {
    if (occupancyHeartbeat) {
        clearInterval(occupancyHeartbeat);
        occupancyHeartbeat = undefined;
    }
    if (occupancyTimer) {
        clearTimeout(occupancyTimer);
        occupancyTimer = undefined;
    }
    lastReportedCount = undefined;
}
/**
 * 页面被挂起/关闭时兜底离会（移动端返回、杀页常见路径）。.
 *
 * @returns {void}
 */
function onPageHide() {
    reportPortalLeave('pagehide');
}
/**
 * 绑定 pagehide / beforeunload 一次。.
 *
 * @returns {void}
 */
function ensurePageHideListener() {
    if (pageHideBound || typeof window === 'undefined') {
        return;
    }
    pageHideBound = true;
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('beforeunload', onPageHide);
}
/**
 * 网页端：离会同步 leave；会中人数变化/心跳同步 occupancy。.
 */
MiddlewareRegistry.register(store => next => action => {
    const result = next(action);
    switch (action.type) {
        case CONFERENCE_JOINED:
            leaveReported = false;
            resetHostEndedForAll();
            ensurePageHideListener();
            startOccupancyHeartbeat(store);
            scheduleOccupancyReport(store, 'conferenceJoined', true);
            break;
        case PARTICIPANT_JOINED:
        case PARTICIPANT_LEFT:
            scheduleOccupancyReport(store, String(action.type));
            break;
        case CONFERENCE_WILL_LEAVE:
        case CONFERENCE_LEFT:
        case CONFERENCE_FAILED:
        case KICKED_OUT: {
            const local = getLocalParticipant(store.getState());
            const replaced = Boolean(local?.isReplaced);
            reportPortalLeave(String(action.type), { skipIfReplaced: replaced });
            break;
        }
    }
    return result;
});
